// src/App.js
import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { gaPageView } from './analytics';
import TeamDashboard from './components/TeamDashboard'; // your team dashboard component
import Register from './components/Register';             // your registration component
import Leaderboard from './components/Leaderboard';
import ProtectedRoute from './components/ProtectedRoute';
import DashboardLayout from './components/DashboardLayout';
import AdminDashboard from './components/AdminDashboard';
import EventManagement from './components/EventManagement';
import BonusPuzzleManagement from './components/BonusPuzzleManagement';
import JumpItStationManager from './components/JumpItStationManager';
import TeamManagement from './components/TeamManagement';
import FlyerManagement from './components/FlyerManagement';
import BonusPuzzleScoring from './components/BonusPuzzleScoring';
import RedirectPuzzle from './components/RedirectPuzzle';
import SkyScoreAdmin from './components/SkyScoreAdmin';
import TestData from './components/TestData'; // Import the Test Data component
import ScoreImport from './components/ScoreImport';
import TeamExport from './components/TeamExport';
import TeamPairing from './components/TeamPairing';
import LoadOrder from './components/LoadOrder';
import EventDayManifest from './components/EventDayManifest';
import BonusScoreAdmin from './components/BonusScoreAdmin';
import SchedulePage from './components/SchedulePage';
import MarketingRedirect from './components/MarketingRedirect';
import PublicDashboardEntry from './components/PublicDashboardEntry';
import YearLiveToDashboardRedirect from './components/YearLiveToDashboardRedirect';
import UserManagement from './components/UserManagement';
import TicketManagement from './components/TicketManagement';
import Printables from './components/Printables';
import BackupManagement from './components/BackupManagement';
import FuryLab from './components/FuryLab';
import TShirtManagement from './components/TShirtManagement';
import RecurrencyAdmin from './components/RecurrencyAdmin';
import RecurrencyUpdate from './components/RecurrencyUpdate';
import EmailTemplatesAdmin from './components/EmailTemplatesAdmin';
import SponsorManagement from './components/SponsorManagement';
import WhatCanIWin from './components/WhatCanIWin';
import FreshMeetHqDashboard from './components/FreshMeetHqDashboard';
import EventMemoryBook from './components/EventMemoryBook';
import AwardsCeremony from './components/AwardsCeremony';
import PhotoCapture from './components/PhotoCapture';
import TeamIdCards from './components/TeamIdCards';
import PublicMorningLoadOrder from './components/PublicMorningLoadOrder';
import SiteEntry from './components/SiteEntry';
import FreshMeetSiteCms from './components/FreshMeetSiteCms';
import YearExperienceHub from './components/YearExperienceHub';
import VolunteerAuthGate from './components/VolunteerAuthGate';
import VolunteerSignup from './components/VolunteerSignup';
import VolunteerShiftAdmin from './components/VolunteerShiftAdmin';
import VideoJudgeQueue from './components/VideoJudgeQueue';
import { AdminPermissionsProvider } from './contexts/AdminPermissionsContext';

/** Legacy /:year/pre → /:year (hub picks live vs archive). */
function RedirectYearPreToHub() {
  const { year } = useParams();
  return <Navigate to={`/${year}`} replace />;
}

function RedirectLegacyEventPath() {
  const { eventId } = useParams();
  return <Navigate to={`/registration?eventId=${encodeURIComponent(eventId || '')}`} replace />;
}

function RedirectAdminEventDashboard() {
  const { id } = useParams();
  return <Navigate to={`/admin/events/${encodeURIComponent(id || '')}`} replace />;
}

function RedirectAdminEventSettings() {
  const { id } = useParams();
  return <Navigate to={`/admin/events/${encodeURIComponent(id || '')}/builder`} replace />;
}

function GaRouteListener() {
  const location = useLocation();
  useEffect(() => {
    gaPageView(`${location.pathname}${location.search}`);
  }, [location.pathname, location.search]);
  return null;
}

function App() { 
  return (
    <Router>
      <GaRouteListener />
      <Routes>
        {/* Public routes */}
        <Route path="/" element={<SiteEntry />} />
        <Route path="/register" element={<Register />} />
        <Route path="/registration" element={<Register />} />
        <Route path="/event/:eventId" element={<RedirectLegacyEventPath />} />
        <Route path="/team-dashboard" element={<TeamDashboard />} />
        <Route path="/leaderboard" element={<Leaderboard />} />
        <Route path="/dashboard" element={<PublicDashboardEntry />} />
        <Route path="/rules" element={<MarketingRedirect pathKey="rules" />} />
        <Route path="/schedule/:scheduleSlug" element={<SchedulePage />} />
        <Route path="/schedule" element={<SchedulePage />} />
        <Route path="/map" element={<MarketingRedirect pathKey="map" />} />
        <Route path="/what-can-i-win" element={<WhatCanIWin />} />
        <Route path="/recurrency-update" element={<RecurrencyUpdate />} />
        <Route path="/memory-book" element={<Navigate to="/" replace />} />
        <Route path="/morning-load-order" element={<PublicMorningLoadOrder />} />
        <Route
          path="/volunteer"
          element={
            <VolunteerAuthGate>
              <VolunteerSignup />
            </VolunteerAuthGate>
          }
        />
        <Route path="/judge" element={<Navigate to="/admin/skyscore" replace />} />
        {/* Short entry routes for bonus puzzles; they capture the code and then hide it from the visible URL.
            The optional :stationId variant is used by jumpItOrFixIt puzzles, where each physical station
            has its own QR. RedirectPuzzle forwards the stationId to /puzzle via route state. */}
        <Route path="/p/:publicCode/:stationId" element={<RedirectPuzzle />} />
        <Route path="/p/:publicCode" element={<RedirectPuzzle />} />
        <Route path="/puzzle/:publicCode/:stationId" element={<RedirectPuzzle />} />
        <Route path="/puzzle/:publicCode" element={<RedirectPuzzle />} />
        <Route path="/puzzle" element={<BonusPuzzleScoring />} />
        <Route path="/:year/schedule/:scheduleSlug" element={<SchedulePage />} />
        <Route path="/:year/schedule" element={<SchedulePage />} />
        <Route path="/:year/rules" element={<MarketingRedirect pathKey="rules" />} />
        <Route path="/:year/map" element={<MarketingRedirect pathKey="map" />} />
        <Route path="/:year/leaderboard" element={<Leaderboard />} />
        <Route path="/:year/pre" element={<RedirectYearPreToHub />} />
        <Route path="/:year/live" element={<YearLiveToDashboardRedirect />} />
        <Route path="/:year/archive/*" element={<YearExperienceHub mode="archive" />} />
        <Route path="/:year" element={<YearExperienceHub mode="index" />} />
        
        {/* Admin routes (protected) */}
        <Route path="/admin" element={
          <ProtectedRoute>
            <AdminPermissionsProvider>
              <DashboardLayout />
            </AdminPermissionsProvider>
          </ProtectedRoute>
        }>
          <Route index element={<AdminDashboard />} />
          <Route path="events" element={<EventManagement />} />
          <Route path="events/:id/dashboard" element={<RedirectAdminEventDashboard />} />
          <Route path="events/:id/settings" element={<RedirectAdminEventSettings />} />
          <Route path="events/:id/builder" element={<Navigate to="/admin/events" replace />} />
          <Route path="events/:id" element={<Navigate to="/admin/events" replace />} />
          <Route path="live/:year" element={<YearExperienceHub mode="live" adminOnly />} />
          <Route path="bonus-puzzle-management" element={<BonusPuzzleManagement />} />
          <Route path="jump-it-stations" element={<JumpItStationManager />} />
          <Route path="bonus-scores" element={<BonusScoreAdmin />} />
          <Route path="teams" element={<TeamManagement />} />
          <Route path="team-pairing" element={<TeamPairing />} />
          <Route path="load-order" element={<LoadOrder />} />
          <Route path="event-manifest" element={<EventDayManifest />} />
          <Route path="fury-lab" element={<FuryLab />} />
          <Route path="recurrencies" element={<RecurrencyAdmin />} />
          <Route path="tickets" element={<TicketManagement />} />
          <Route path="tshirts" element={<TShirtManagement />} />
          <Route path="printables" element={<Printables />} />
          <Route path="event-memory-book" element={<EventMemoryBook />} />
          <Route path="photo-capture" element={<PhotoCapture />} />
          <Route path="team-id-cards" element={<TeamIdCards />} />
          <Route path="awards-ceremony" element={<AwardsCeremony />} />
          <Route path="sponsors" element={<SponsorManagement />} />
          <Route path="freshmeet-hq" element={<FreshMeetHqDashboard />} />
          <Route path="main-clue-sponsor-run" element={<Navigate to="/admin/freshmeet-hq?tab=sponsor-run" replace />} />
          <Route path="flyer-management" element={<FlyerManagement />} />
          <Route path="skyscore" element={<SkyScoreAdmin />} />
          <Route path="score-import" element={<ScoreImport />} />
          <Route path="test-data" element={<TestData />} /> {/* Add the Test Data route */}
          <Route path="team-export" element={<TeamExport />} />
          <Route path="users" element={<UserManagement />} />
          <Route path="backups" element={<BackupManagement />} />
          <Route path="templates" element={<EmailTemplatesAdmin />} />
          <Route path="freshmeet-site" element={<FreshMeetSiteCms />} />
          <Route path="volunteers" element={<VolunteerShiftAdmin />} />
          <Route path="video-judge-queue" element={<VideoJudgeQueue />} />
        </Route>
        
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
  );
}

export default App;
